import axios from "axios";
import pubsub from "@viewlift/pubsub"
import { config } from '../../../../server/config'
import { getToken } from "../../../helpers"
import { parseJSON } from '../../utils/parseJSON'
import { publishAppLoading } from '../../Events/utils.events'
import { ANALYTICSEVENTS } from '../../LoginV2/Helpers/analyticsMap'
import { subscriptionRazorpayMutation, upgradeSubscriptionRazorpay, cancelSubscriptionRazorpay, revertSubscriptionRazorpay } from "../../../Graphql/Queries/razorPayQuery"
import { handleStatusGaCalls, verifyUserAccount, pushStepToFlow } from '../../helper'
import { subscribeSubscriptionStatusEvent, subscriptionCancelledEvent, subscriptionRevertedEvent } from '../../Events/monetisation.events'
import { subscriptionStore } from '../../../MFEs/Monetization-Sdk/src/Api'
import { isUpgradePlan } from '../helper'

const apiKey = window?.app_data?.appcmsMain?.paymentProviders?.razorpay?.apiKey;
const merchantLogo = window?.app_data?.appcmsMain?.paymentProviders?.razorpay?.merchantLogo;
const themeColor = window?.app_data?.appcmsMain?.brand?.cta?.primary?.backgroundColor;

export const loadRazorPayScript = () => {
    return new Promise((resolve, reject) => {
        if (window.Razorpay) {
            resolve() // Razorpay already loaded
            return
        }
        const script = document.createElement("script")
        script.src = "https://checkout.razorpay.com/v1/checkout.js"
        script.async = true
        script.onload = () => resolve()

        script.onerror = () => {
            reject(new Error("Failed to load Razorpay SDK"))
            document.body.removeChild(script)
        }
        document.body.appendChild(script)
    });
}

export const createOrder = async ({ userId, emailId, phoneNumber }) => {
    const apiUrl = `${config?.apiBaseUrl}/v3/razorpay/subscription/order/create`;
    const token = await getToken();
    const currentPlanId = parseJSON(sessionStorage.getItem('currentPlan'))?.planId;
    const offerCode = sessionStorage.getItem('offerCode');

    if (!currentPlanId) {
        throw new Error("Current plan is not set.");
    }

    const postData = {
        site: window?.app_data?.site?.siteInternalName || null,
        userId,
        planId: currentPlanId,
        platform: "web_browser",
        isUpgrade: isUpgradePlan(),
        prefill: {
            contact: phoneNumber,
            email: emailId,
        },
        offerCode,
    };

    const headerConfig = {
        headers: {
            'Authorization': token,
            'x-api-key': window?.xApiKey,
        }
    };

    try {
        const response = await axios.post(apiUrl, postData, headerConfig);
        return response?.data || {};
    } catch (errorMsg) {
        console.error("Failed to create order:", errorMsg);
        return ("Failed to create order:" + errorMsg.response?.data?.error || errorMsg.message);
    }
}

export const initiateRazorpayPayment = async ({ emailId, phoneNumber, orderId, amount, currency, receipt }) => {
    let currentPlan = parseJSON(sessionStorage.getItem('currentPlan')) || null;
    const options = {
        key: apiKey, // Replace with your Razorpay Key ID
        amount: amount, // in paise = INR 500
        currency: currency,
        image: merchantLogo,
        order_id: orderId,
        handler: async function (response) {
            sessionStorage.setItem('razorpayResponse', JSON.stringify(response));
            userClickedEvents({ orderId, paymentStatus: 'success' });
            await handleCreateAccountAndSubscription(response, receipt);
        },
        prefill: {
            email: emailId,
            contact: phoneNumber,
        },
        notes: {
            address: "Corporate Office",
        },
        theme: {
            color: themeColor,
        },
        recurring: (currentPlan?.renewable) || false,
    };
    console.log("Razorpay options:", options);
    const rzp = new window.Razorpay(options);
    rzp.open()

    const paymentFailedHandler = async function (response) {
        console.error("payment.failed Response :", response);

        userClickedEvents({ orderId, paymentStatus: 'failure' });
        handleStatusGaCalls({ eventName: 'payment_failure', subSection: null, type: 'failure', reason: `Payment Failure and OrderId: ${optionsInput.orderId}, Payment Failed Reason: ${response?.error.description}, Error Details: ${response?.error}`, status: 'failure ', action: 'Failure', paymentStatus: 'failure', eventCategory: 'Payment', paymentHandler: 'Razorpay' })
    }
    rzp.on("payment.failed", paymentFailedHandler)
    return () => {
        rzp.off("payment.failed", paymentFailedHandler);
    }
}

const handleCreateAccountAndSubscription = async (response, receipt) => {
    publishAppLoading(true)
    console.log("payment Response :", response);
    let optionsInput = {}

    verifyUserAccount()
        .then((userCreatedRes) => {
            if (userCreatedRes?.userCreated || userCreatedRes?.userAlreadyCreated) {

                if (userCreatedRes?.email || userCreatedRes?.phoneNumber) {
                    let currentPlan = parseJSON(sessionStorage.getItem('currentPlan')) || null;

                    optionsInput["userId"] = userCreatedRes?.id;
                    optionsInput["receipt"] = receipt;
                    optionsInput["email"] = userCreatedRes?.email;
                    optionsInput["phoneNumber"] = userCreatedRes?.phoneNumber || userCreatedRes?.phone || null;
                    optionsInput["orderId"] = response?.razorpay_order_id;
                    optionsInput["paymentUniqueId"] = response?.razorpay_payment_id;
                    optionsInput["signature"] = response?.razorpay_signature;
                    optionsInput["planId"] = currentPlan?.planId || null;
                    optionsInput["planIdentifier"] = currentPlan?.identifier || null;
                    optionsInput["currencyCode"] = currentPlan?.currency || null;
                    optionsInput["platform"] = "web_browser";
                    optionsInput["subscription"] = "razorpay";

                    if (isUpgradePlan()) {
                        // If it's an upgrade plan, call the upgradeSubscription mutation
                        upgradeSubscription(optionsInput)
                            .then((upgradeRes) => {
                                if (upgradeRes?.success === 'COMPLETED') {
                                    handleSubscriptionCompleted(optionsInput, upgradeRes)
                                } else {
                                    handleSubscriptionNotCompleted(optionsInput, upgradeRes)
                                }
                            })
                            .catch((error) => {
                                handleSubscriptionError(optionsInput, error)
                            });
                    } else {
                        // If not an upgrade plan, create a new subscription
                        subscriptionRazorpay(optionsInput)
                            .then((subscriptionRes) => {
                                const subscriptionStatus = subscriptionRes?.subscription?.subscriptionStatus
                                if (subscriptionStatus === 'COMPLETED') {
                                    handleSubscriptionCompleted(optionsInput, subscriptionRes)
                                } else {
                                    handleSubscriptionNotCompleted(optionsInput, subscriptionRes)
                                }
                            })
                            .catch((error) => {
                                handleSubscriptionError(optionsInput, error)
                            });
                    }
                } else {
                    handleUserNotFound(optionsInput, userCreatedRes)
                }
            }
        }).catch((error) => {
            console.error("Error in verifyUserAccount:", error);
            pushStepToFlow('subscribe_failed')
            publishAppLoading(false)
        })
}


const subscriptionRazorpay = async (optionsInput = {}) => {
    const graphqlUrl = `${config.apiBaseUrl}/graphql`
    const token = await getToken()

    return axios.post(graphqlUrl,
        {
            query: subscriptionRazorpayMutation,
            variables: {
                "site": (window?.app_data?.site?.siteInternalName) || null,
                "input": {
                    "razorpay": optionsInput
                }
            }
        },
        {
            headers: {
                'Authorization': token,
                'x-api-key': window?.xApiKey,
            }
        })
        .then(response => {
            return response?.data?.data?.createAccountAndSubscription || response?.data?.errors || {};
        })
        .catch(error => {
            console.error("GraphQL mutation error:", error);
        });
}
const upgradeSubscription = async (optionsInput = {}) => {
    const graphqlUrl = `${config.apiBaseUrl}/graphql`
    const token = await getToken()

    return axios.post(graphqlUrl,
        {
            query: upgradeSubscriptionRazorpay,
            variables: {
                "site": (window?.app_data?.site?.siteInternalName) || null,
                "input": {
                    "razorpay": optionsInput
                }
            }
        },
        {
            headers: {
                'Authorization': token,
                'x-api-key': window?.xApiKey,
            }
        })
        .then(response => {
            return response?.data?.data?.upgradeSubscription || response?.data?.errors || {};
        })
        .catch(error => {
            console.error("GraphQL mutation error:", error);
        });
}

const handleSubscriptionCompleted = (optionsInput, res) => {
    console.log("Razorpay Subscription response:", res);
    pubsub.publish('user-analytics', { ename: ANALYTICSEVENTS.PAYMENT_SUCCCESS, data: optionsInput });

    handleSuccessEvents('checkout_success', eventCategory = 'Checkout', reason = `Payment Success and paymentUniqueId: ${optionsInput.paymentUniqueId} and OrderId: ${optionsInput.orderId}`)
    handleSuccessEvents('payment_success', eventCategory = 'Payment', reason = `Payment Success and paymentUniqueId: ${optionsInput.paymentUniqueId} and OrderId: ${optionsInput.orderId}`)
    handleSuccessEvents('registration', eventCategory = 'Payment', reason = `Payment Success and paymentUniqueId: ${optionsInput.paymentUniqueId} and OrderId: ${optionsInput.orderId}`)

    subscribeSubscriptionStatusEvent(onCompletedSubscriptionSSE)
    pushStepToFlow("payment-success")
}
const onCompletedSubscriptionSSE = (data) => {
    /**  Received data payload from sse
    {
        "type": 47,
        "site": "myoutdoortv",
        "userMeta": {
            "id": "35752591-97b1-436a-9b02-c9ebfbd833ba"
        },
        "payload": {
            "paymentHandler": "RAZORPAY",
            "subscriptionStatus": "COMPLETED"
        },
        "notificationType": "SUBSCRIPTION_CREATE_SUCCESS"
    }
    */
    if (data?.subscriptionStatus === "COMPLETED") {
        sessionStorage.getItem('offerCode') ? sessionStorage.removeItem('offerCode') : ''
        subscriptionStore(null, null, true)
        setTimeout(() => {
            publishAppLoading(false);
        }, 1000);
    } else {
        setTimeout(() => {
            subscriptionStore(null, null, false)
            publishAppLoading(false);
        }, 10000)
    }
}
const handleSubscriptionNotCompleted = (optionsInput, res) => {
    console.error("Razorpay Subscription status not found. Response:", res);

    pubsub.publish('user-analytics', { ename: ANALYTICSEVENTS.PAYMENT_FAILURE, data: optionsInput });

    handleFailedEvents('checkout_error', eventCategory = 'Payment', reason = `Payment Failure and paymentUniqueId: ${optionsInput.paymentUniqueId} and OrderId: ${optionsInput.orderId}`)
    handleFailedEvents('payment_failure', eventCategory = 'Payment', reason = `Payment Failure and paymentUniqueId: ${optionsInput.paymentUniqueId} and OrderId: ${optionsInput.orderId}`)

    publishAppLoading(false);
}

export const handleCancelSubscription = async (userId) => {
    // publishAppLoading(true)
    let optionsInput = {}

    optionsInput["site"] = window?.app_data?.site?.siteInternalName;
    optionsInput["userId"] = userId;
    return cancelSubscription(optionsInput)
    .then((cancelRes) => {
        if (cancelRes?.success) {
            onSubscriptionCancelled(optionsInput, cancelRes)
        } else {
            onSubscriptionNotCancelled(optionsInput, cancelRes)
        }
        return cancelRes
    })
    .catch((error) => {
        handleSubscriptionError(optionsInput, error)
    });
}
const cancelSubscription = async (optionsInput = {}) => {
    const graphqlUrl = `${config.apiBaseUrl}/graphql`
    const token = await getToken()

    return axios.post(graphqlUrl,
        {
            query: cancelSubscriptionRazorpay,
            variables: {
                "site": (window?.app_data?.site?.siteInternalName) || null,
                "input": {
                    "razorpay": optionsInput
                }
            }
        },
        {
            headers: {
                'Authorization': token,
                'x-api-key': window?.xApiKey,
            }
        })
        .then(response => {
            return response?.data?.data?.cancelSubscription || response?.data?.errors || {};
        })
        .catch(error => {
            console.error("GraphQL mutation error:", error);
        });
}

const onSubscriptionCancelled = (optionsInput, res) => {
    console.log("Razorpay Subscription Cancel response:", res);

    handleCancelSubscriptionEvents('cancel_subscription', eventCategory = 'Subscription', reason = `Cancel Subscription of user id: ${optionsInput.userId}`, 'Cancel')
    subscriptionCancelledEvent(onCancelSubscriptionSSE)
}
const onSubscriptionNotCancelled = (optionsInput, res) => {
    console.log("Razorpay Subscription not Cancel response:", res);

    handleCancelSubscriptionEvents('cancel_subscription', eventCategory = 'Subscription', reason = `Cancel Subscription of user id: ${optionsInput.userId}`, 'Not Cancel')
    // subscriptionCancelledEvent(onCancelSubscriptionSSE)
    publishAppLoading(false);
}

const onCancelSubscriptionSSE = (data) => {
    /**  Received data payload from sse
    {
        "type": 51,
        "site": "myoutdoortv",
        "userMeta": {
            "id": "c7af5d60-b527-474a-b73c-e0762e95e81f"
        },
        "payload": {
            "paymentHandler": "RAZORPAY",
            "subscriptionStatus": "CANCELLED"
        },
        "notificationType": "USER_SUBSCRIPTION_CANCELLED"
    }
    */
    if (data?.subscriptionStatus === "CANCELLED") {
        setTimeout(() => {
            publishAppLoading(false);
        }, 1000);
    } else {
        setTimeout(() => {
            publishAppLoading(false);
        }, 10000)
    }
}

export const handleRevertSubscription = (userId) => {
    // publishAppLoading(true)
    let optionsInput = {}

    optionsInput["site"] = window?.app_data?.site?.siteInternalName;
    optionsInput["userId"] = userId;
    return revertSubscription(optionsInput)
    .then((revertRes) => {
        if (revertRes?.success) {
            onSubscriptionRevert(optionsInput, revertRes)
        } else {
            onSubscriptionNotRevert(optionsInput, revertRes)
        }
        return revertRes
    })
    .catch((error) => {
        handleSubscriptionError(optionsInput, error)
    });
}
const revertSubscription = async (optionsInput = {}) => {
    const graphqlUrl = `${config.apiBaseUrl}/graphql`
    const token = await getToken()

    return axios.post(graphqlUrl,
        {
            query: revertSubscriptionRazorpay,
            variables: {
                "site": (window?.app_data?.site?.siteInternalName) || null,
                "input": {
                    "razorpay": optionsInput
                }
            }
        },
        {
            headers: {
                'Authorization': token,
                'x-api-key': window?.xApiKey,
            }
        })
        .then(response => {
            return response?.data?.data?.revertSubscription || response?.data?.errors || {};
        })
        .catch(error => {
            console.error("GraphQL mutation error:", error);
        });
}

const onSubscriptionRevert = (optionsInput, res) => {
    console.log("Razorpay Subscription Revert response:", res);

    handleCancelSubscriptionEvents('revert_subscription', eventCategory = 'Subscription', reason = `Revert Subscription of user id: ${optionsInput.userId}`, 'Revert')
    subscriptionRevertedEvent(onRevertSubscriptionSSE)
}
const onSubscriptionNotRevert = (optionsInput, res) => {
    console.log("Razorpay Subscription not Revert response:", res);

    handleCancelSubscriptionEvents('revert_subscription', eventCategory = 'Subscription', reason = `Revert Subscription of user id: ${optionsInput.userId}`, 'Not Revert')
    publishAppLoading(false);
}
const onRevertSubscriptionSSE = async (data) => {
    /**  Received data payload from sse
    {
        "type": 1,
        "site": "myoutdoortv",
        "userMeta": {
            "id": "2deb565c-ce48-4d6d-b725-5cf9ad7fa5fc"
        },
        "payload": {"paymentHandler":"RAZORPAY","subscriptionStatus":"COMPLETED"},
        "notificationType": "USER_SUBSCRIPTION_COMPLETED"
    }
    */
    if (data?.subscriptionStatus === "COMPLETED") {        
        setTimeout(() => {
            publishAppLoading(false);
        }, 1000);
    } else {
        setTimeout(async () => {
            publishAppLoading(false);
        }, 10000)
    }
}
const handleSubscriptionError = (optionsInput, error) => {
    console.error("Error in subscriptionRazorpay:", error);

    pubsub.publish('user-analytics', { ename: ANALYTICSEVENTS.PAYMENT_FAILURE, data: optionsInput });

    handleFailedEvents('checkout_error', eventCategory = 'Payment', reason = `Payment Failure and paymentUniqueId: ${optionsInput.paymentUniqueId} and OrderId: ${optionsInput.orderId}`)
    handleFailedEvents('payment_failure', eventCategory = 'Payment', reason = `Payment Failure and paymentUniqueId: ${optionsInput.paymentUniqueId} and OrderId: ${optionsInput.orderId}`)

    pushStepToFlow('subscribe_failed')
    publishAppLoading(false);
}

const handleUserNotFound = (optionsInput, userCreatedRes) => {
    console.error("User email or phone number not found", userCreatedRes);
    handleFailedEvents('checkout_error', eventCategory = 'Payment', reason = `Payment Failure and OrderId: ${optionsInput.orderId}, User email or phone number not found.`)
    handleFailedEvents('payment_failure', eventCategory = 'Payment', reason = `Payment Failure and OrderId: ${optionsInput.orderId}, User email or phone number not found.`)

    pushStepToFlow('subscribe_failed')
    publishAppLoading(false)
}
const handleSuccessEvents = (eventName, eventCategory = 'Checkout', reason) => {
    handleStatusGaCalls({ eventName, subSection: null, type: 'success', reason, status: 'COMPLETED', action: 'Success', paymentStatus: 'success', eventCategory, paymentHandler: 'Razorpay' })
}

const handleFailedEvents = (eventName, eventCategory = 'Checkout', reason) => {
    handleStatusGaCalls({ eventName, subSection: null, type: 'failure', reason, status: 'FAILED', action: 'Failure', paymentStatus: 'failed', eventCategory, paymentHandler: 'Razorpay' })
}
const handleCancelSubscriptionEvents = (eventName, eventCategory, reason, action) => {
    handleStatusGaCalls({ eventName, subSection: null, type: 'cancelSubscription', reason: reason, status: null, additionalLabel: null, action: action, paymentStatus: null, eventCategory: eventCategory, section: 'Subscription', paymentHandler: 'Razorpay' })
}

const userClickedEvents = ({ orderId, paymentStatus }) => {
    let currentPlan = parseJSON(sessionStorage.getItem('currentPlan'))
    let subscribeBtnEventData = {
        "planId": currentPlan?.planId,
        "planName": currentPlan?.title,
        "planDesc": currentPlan?.description,
        "planType": currentPlan?.type,
        "paymentMethod": null,
        "discountAmount": currentPlan?.plansDetail?.recurringPaymentAmount - currentPlan?.plansDetail?.strikeThroughPrice,
        "monetizationType": currentPlan?.type === "Buy" ? "PURCHASE" : currentPlan?.type?.toUpperCase(),
        "orderSubTotalAmount": currentPlan?.amount,
        "orderTotalAmount": currentPlan?.amount,
        "currencyCode": currentPlan?.plansDetail?.recurringPaymentCurrencyCode,
        "orderId": orderId
    }
    pubsub.publish('user-analytics', { ename: ANALYTICSEVENTS.SUBSCRIBE_CLICKED, data: subscribeBtnEventData });
    handleStatusGaCalls({ eventName: 'subscribe_clicked', subSection: null, type: 'subscribe', reason: currentPlan?.type, status: null, action: 'Subscribe Now', paymentStatus: paymentStatus, eventCategory: 'Subscription', paymentHandler: 'Razorpay' })
}